import { useState, useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import {
  TrendingUp, TrendingDown, Minus, DollarSign, ShoppingCart, FileText, AlertTriangle,
  Clock, Package, Boxes, CheckCircle2, ArrowUpRight, ArrowDownRight, Users,
  Activity, AlertCircle, Flame, BarChart3, PieChart as PieIcon, Calendar,
  Truck, PackageCheck, Sparkles, Info, Loader2,
} from 'lucide-react'
import {
  LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
  BarChart, Bar, Cell, PieChart, Pie, Legend,
} from 'recharts'
import { api, formatMoney } from '@/lib/api'
import CanalTag from '@/components/CanalTag'
import { useMostrarCanalTag } from '@/lib/useCanalTag'
import { getDashEstilo, EVT, type DashEstilo } from '@/lib/aparencia'

interface Kpi { valor: number; qtd_pedidos?: number; ticket_medio?: number; variacao_pct: number | null; comparacao_label: string }
interface Alerta { severidade: string; tipo: string; titulo: string; descricao: string; link: string; qtd: number | null }
interface Dash {
  gerado_em: string
  kpis: {
    vendas_hoje: Kpi
    vendas_mes: Kpi
    vendas_30d: Kpi
    vendas_mes_anterior: Kpi
    pedidos_fluxo: { abertos: number; em_separacao: number; em_embalagem: number; aguardando_nf: number; faturados: number; expedidos: number }
    nfs: { pendentes: number; rejeitadas: number; autorizadas_hoje: number }
    financeiro: { a_pagar_prox_3d: number; qtd_a_pagar_prox_3d: number; a_receber_prox_3d: number; qtd_a_receber_prox_3d: number; a_pagar_atrasado: number; a_receber_atrasado: number }
  }
  alertas: Alerta[]
  estoque_baixo: Array<{ id: number; codigo: string; descricao: string; estoque_atual: number; estoque_minimo: number }>
  top_produtos: Array<{ produto_id: number; codigo: string; descricao: string; qtd_vendida: number; receita: number }>
  serie_vendas: Array<{ dia: string; receita: number; qtd_pedidos: number }>
  canais: Array<{ canal: string; receita: number; qtd_pedidos: number }>
  ultimos_pedidos: Array<{ id: number; numero: number; status: string; total: number; criado_em: string; canal_origem: string; caixa_canal?: string | null; cliente_nome: string | null }>
}

const CORES_CANAL = ['#3b82f6', '#8b5cf6', '#10b981', '#f59e0b', '#ef4444', '#06b6d4']
const STATUS_COR: Record<string, string> = {
  aberto: 'text-ink-600 bg-ink-100',
  aprovado: 'text-blue-700 bg-blue-100',
  separacao: 'text-purple-700 bg-purple-100',
  embalagem: 'text-indigo-700 bg-indigo-100',
  aguardando_nf: 'text-orange-700 bg-orange-100',
  faturado: 'text-teal-700 bg-teal-100',
  expedido: 'text-cyan-700 bg-cyan-100',
  entregue: 'text-emerald-700 bg-emerald-100',
  cancelado: 'text-red-700 bg-red-100',
  confirmado: 'text-emerald-700 bg-emerald-50',
}

interface GraficosFin {
  pagar_aging: Array<{ faixa: string; valor: number }>
  conciliacao: { conciliados: number; pendentes: number }
  bancos: Array<{ nome: string; saldo: number; cor: string | null }>
  receber_aging?: Array<{ faixa: string; valor: number }>
  receber_por_forma?: Array<{ forma: string; label: string; qtd: number; valor: number }>
  receber_resumo?: {
    total_qt: number; total_vl: number
    atrasado_qt: number; atrasado_vl: number
    proximos_7d_qt: number; proximos_7d_vl: number
    proximos_15d_qt: number; proximos_15d_vl: number
  }
  top_inadimplentes?: Array<{ pessoa_id: number; nome: string; valor: number }>
}

export default function Dashboard() {
  const mostrarCanalTag = useMostrarCanalTag()   // tag Loja×Online na visão "Ambos" (09/07)
  const { data, isLoading } = useQuery<Dash>({
    queryKey: ['analytics-dashboard'],
    queryFn: async () => (await api.get('/analytics/dashboard')).data,
    refetchInterval: 60_000, // atualiza cada 60s
  })

  const { data: fin } = useQuery<GraficosFin>({
    queryKey: ['financeiro-graficos-dashboard'],
    queryFn: async () => (await api.get('/financeiro/graficos-dashboard')).data,
    refetchInterval: 60_000,
  })

  const [dashEstilo, setDash] = useState<DashEstilo>(() => getDashEstilo())
  useEffect(() => {
    const s = () => setDash(getDashEstilo())
    window.addEventListener(EVT, s)
    return () => window.removeEventListener(EVT, s)
  }, [])
  const compacto = dashEstilo === 'compacto'
  const executivo = dashEstilo === 'executivo'

  if (isLoading || !data) {
    return (
      <div className="p-10 flex items-center justify-center text-ink-400">
        <Loader2 className="animate-spin mr-2" size={18} /> Carregando visão geral...
      </div>
    )
  }

  return (
    <div className={`${compacto ? 'p-3 lg:p-4 space-y-3' : 'p-4 lg:p-6 space-y-5'}`}>
      {executivo ? (
        <HeroExecutivo data={data} />
      ) : (
        <>
          {/* Cabeçalho */}
          <div className="flex items-center justify-between">
            <div>
              <h1 className={`font-bold text-ink-900 flex items-center gap-2 ${compacto ? 'text-xl' : 'text-2xl'}`}>
                <BarChart3 size={compacto ? 20 : 24} /> Visão Geral do Negócio
              </h1>
              {!compacto && (
                <p className="text-sm text-ink-500">
                  Atualizado {new Date(data.gerado_em).toLocaleTimeString('pt-BR')} · atualiza automaticamente
                </p>
              )}
            </div>
          </div>

          {/* KPIs principais */}
          <div className={`grid grid-cols-2 lg:grid-cols-4 ${compacto ? 'gap-2' : 'gap-4'}`}>
            <KpiCard
              titulo="Vendas hoje"
              valor={formatMoney(data.kpis.vendas_hoje.valor)}
              subtitulo={`${data.kpis.vendas_hoje.qtd_pedidos} pedidos · ticket ${formatMoney(data.kpis.vendas_hoje.ticket_medio || 0)}`}
              variacao={data.kpis.vendas_hoje.variacao_pct}
              comparacao={data.kpis.vendas_hoje.comparacao_label}
              icon={DollarSign}
              cor="emerald"
              link={qsVendas('hoje')}
            />
            <KpiCard
              titulo="Vendas no mês"
              valor={formatMoney(data.kpis.vendas_mes.valor)}
              subtitulo={`${data.kpis.vendas_mes.qtd_pedidos} pedidos · ticket ${formatMoney(data.kpis.vendas_mes.ticket_medio || 0)}`}
              variacao={data.kpis.vendas_mes.variacao_pct}
              comparacao={data.kpis.vendas_mes.comparacao_label}
              icon={Calendar}
              cor="blue"
              link={qsVendas('mes')}
            />
            <KpiCard
              titulo="Mês anterior"
              valor={formatMoney(data.kpis.vendas_mes_anterior.valor)}
              subtitulo={`${data.kpis.vendas_mes_anterior.qtd_pedidos} pedidos no mês`}
              variacao={data.kpis.vendas_mes_anterior.variacao_pct}
              comparacao={data.kpis.vendas_mes_anterior.comparacao_label}
              icon={TrendingUp}
              cor="violet"
              link={qsVendas('mes_anterior')}
            />
            <KpiCard
              titulo="NFs autorizadas hoje"
              valor={String(data.kpis.nfs.autorizadas_hoje)}
              subtitulo={
                data.kpis.nfs.pendentes || data.kpis.nfs.rejeitadas
                  ? `${data.kpis.nfs.pendentes} pendente(s), ${data.kpis.nfs.rejeitadas} rejeitada(s)`
                  : 'Todas processadas'
              }
              variacao={null}
              comparacao=""
              icon={FileText}
              cor={data.kpis.nfs.rejeitadas > 0 ? 'red' : 'teal'}
              link="/fiscal"
            />
          </div>
        </>
      )}

      {/* Alertas proativos */}
      {data.alertas.length > 0 && (
        <div className="card border-amber-200 bg-amber-50/40 p-4">
          <h2 className="font-bold text-amber-900 flex items-center gap-2 mb-3">
            <AlertCircle size={16} /> Radar de alertas
            <span className="text-xs font-normal text-amber-700">{data.alertas.length} item(ns) requer(em) atenção</span>
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
            {data.alertas.map((a, i) => (
              <Link key={i} to={a.link}
                className={`flex items-start gap-2 p-3 rounded-lg border hover:shadow-sm transition-shadow ${
                  a.severidade === 'alto'
                    ? 'bg-red-50 border-red-200 hover:border-red-300'
                    : 'bg-amber-100/60 border-amber-300 hover:border-amber-400'
                }`}>
                {a.severidade === 'alto'
                  ? <Flame size={16} className="text-red-600 flex-shrink-0 mt-0.5" />
                  : <AlertTriangle size={16} className="text-amber-700 flex-shrink-0 mt-0.5" />
                }
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-ink-900 truncate">{a.titulo}</p>
                  <p className="text-xs text-ink-600">{a.descricao}</p>
                </div>
                <ArrowUpRight size={14} className="text-ink-400 flex-shrink-0 mt-0.5" />
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* Fluxo de pedidos (mini kanban) */}
      <div className="card p-4">
        <h2 className="font-bold text-ink-900 flex items-center gap-2 mb-3">
          <Activity size={16} /> Fluxo operacional
        </h2>
        <div className="grid grid-cols-3 md:grid-cols-6 gap-2">
          <FluxoBadge icon={Clock} label="Abertos" valor={data.kpis.pedidos_fluxo.abertos} cor="ink" />
          <FluxoBadge icon={Package} label="Separação" valor={data.kpis.pedidos_fluxo.em_separacao} cor="purple" />
          <FluxoBadge icon={Boxes} label="Embalagem" valor={data.kpis.pedidos_fluxo.em_embalagem} cor="indigo" />
          <FluxoBadge icon={FileText} label="Aguard. NF" valor={data.kpis.pedidos_fluxo.aguardando_nf} cor="orange" />
          <FluxoBadge icon={PackageCheck} label="Faturados" valor={data.kpis.pedidos_fluxo.faturados} cor="teal" />
          <FluxoBadge icon={Truck} label="Expedidos" valor={data.kpis.pedidos_fluxo.expedidos} cor="cyan" />
        </div>
      </div>

      {/* Gráfico + Top Produtos */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 card p-4">
          <h2 className="font-bold text-ink-900 flex items-center gap-2 mb-3">
            <TrendingUp size={16} /> Vendas dos últimos 30 dias
          </h2>
          {data.serie_vendas.length > 0 ? (
            <div style={{ height: 260 }}>
              <ResponsiveContainer>
                <LineChart data={data.serie_vendas}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis dataKey="dia" tick={{ fontSize: 11 }}
                    tickFormatter={(v) => new Date(v).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })} />
                  <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `R$${v}`} />
                  <Tooltip
                    formatter={(v: any) => [formatMoney(v), 'Receita']}
                    labelFormatter={(v) => new Date(v).toLocaleDateString('pt-BR', { weekday: 'short', day: '2-digit', month: 'short' })}
                  />
                  <Line type="monotone" dataKey="receita" stroke="#3b82f6" strokeWidth={2.5}
                    dot={{ fill: '#3b82f6', r: 3 }} activeDot={{ r: 5 }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <EmptyState icon={BarChart3} msg="Sem vendas nos últimos 30 dias" />
          )}
        </div>

        <div className="card p-4">
          <h2 className="font-bold text-ink-900 flex items-center gap-2 mb-3">
            <Flame size={16} /> Top produtos (30d)
          </h2>
          {data.top_produtos.length > 0 ? (
            <div className="space-y-2">
              {data.top_produtos.map((p, i) => (
                <div key={p.produto_id} className="flex items-center gap-3 p-2 rounded hover:bg-ink-50">
                  <div className={`w-6 h-6 rounded flex items-center justify-center text-xs font-bold
                    ${i === 0 ? 'bg-amber-100 text-amber-900' : i === 1 ? 'bg-ink-200 text-ink-700' : 'bg-ink-100 text-ink-600'}`}>
                    {i + 1}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{p.descricao}</p>
                    <p className="text-xs text-ink-500 font-mono">{p.codigo} · {p.qtd_vendida.toFixed(0)} un</p>
                  </div>
                  <p className="text-sm font-bold text-emerald-700">{formatMoney(p.receita)}</p>
                </div>
              ))}
            </div>
          ) : (
            <EmptyState icon={Package} msg="Sem vendas" />
          )}
        </div>
      </div>

      {/* Canais + Estoque baixo */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="card p-4">
          <h2 className="font-bold text-ink-900 flex items-center gap-2 mb-3">
            <PieIcon size={16} /> Vendas por canal (30d)
          </h2>
          {data.canais.length > 0 ? (
            <div className="flex gap-4 items-center">
              <div style={{ width: 160, height: 160 }}>
                <ResponsiveContainer>
                  <PieChart>
                    <Pie data={data.canais} dataKey="receita" nameKey="canal"
                      cx="50%" cy="50%" innerRadius={35} outerRadius={70}
                      label={(e: any) => `${(e.percent * 100).toFixed(0)}%`} labelLine={false}>
                      {data.canais.map((_, i) => <Cell key={i} fill={CORES_CANAL[i % CORES_CANAL.length]} />)}
                    </Pie>
                    <Tooltip formatter={(v: any) => formatMoney(v)} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="flex-1 space-y-1.5">
                {data.canais.map((c, i) => (
                  <div key={c.canal} className="flex items-center gap-2 text-xs">
                    <span className="w-3 h-3 rounded" style={{ background: CORES_CANAL[i % CORES_CANAL.length] }} />
                    <span className="capitalize flex-1 truncate">{c.canal}</span>
                    <span className="text-ink-600">{c.qtd_pedidos}</span>
                    <span className="font-semibold text-ink-900">{formatMoney(c.receita)}</span>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <EmptyState icon={PieIcon} msg="Sem vendas por canal" />
          )}
        </div>

        <div className="card p-4">
          <h2 className="font-bold text-ink-900 flex items-center gap-2 mb-3">
            <AlertTriangle size={16} className={data.estoque_baixo.length > 0 ? 'text-red-600' : 'text-emerald-600'} />
            Estoque crítico
          </h2>
          {data.estoque_baixo.length > 0 ? (
            <div className="space-y-1.5">
              {data.estoque_baixo.slice(0, 6).map(p => (
                <Link key={p.id} to={`/cadastros/produtos?busca=${p.codigo}`}
                  className="flex items-center gap-2 p-2 rounded hover:bg-ink-50 text-sm">
                  <div className="flex-1 min-w-0">
                    <p className="font-medium truncate">{p.descricao}</p>
                    <p className="text-xs text-ink-500 font-mono">{p.codigo}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-ink-500">estoque</p>
                    <p className="font-bold text-red-600">{p.estoque_atual.toFixed(0)} / {p.estoque_minimo.toFixed(0)}</p>
                  </div>
                </Link>
              ))}
              {data.estoque_baixo.length > 6 && (
                <Link to="/estoque" className="text-xs text-brand-600 hover:underline block pt-1 text-center">
                  Ver todos os {data.estoque_baixo.length}
                </Link>
              )}
            </div>
          ) : (
            <EmptyState icon={CheckCircle2} msg="Todos os produtos com estoque adequado" cor="emerald" />
          )}
        </div>
      </div>

      {/* Últimos pedidos */}
      <div className="card p-4">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-bold text-ink-900 flex items-center gap-2">
            <ShoppingCart size={16} /> Últimos pedidos
          </h2>
          <Link to="/vendas" className="text-xs text-brand-600 hover:underline flex items-center gap-1">
            Ver todos <ArrowUpRight size={12} />
          </Link>
        </div>
        <div className="divide-y divide-ink-100">
          {data.ultimos_pedidos.map(p => (
            <Link key={p.id} to="/vendas"
              className="flex items-center gap-3 p-2 hover:bg-ink-50 -mx-2 px-2 rounded">
              <span className="text-xs font-mono text-ink-500 w-12">#{p.numero}</span>
              {mostrarCanalTag && <CanalTag canal={p.caixa_canal} />}
              <span className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{p.cliente_nome || '—'}</p>
                <p className="text-xs text-ink-500">{new Date(p.criado_em).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' })}</p>
              </span>
              <span className={`text-[10px] font-semibold uppercase px-2 py-0.5 rounded ${STATUS_COR[p.status] || 'bg-ink-100'}`}>
                {p.status.replace('_', ' ')}
              </span>
              <span className="text-sm font-bold text-ink-900 w-24 text-right">{formatMoney(p.total)}</span>
            </Link>
          ))}
        </div>
      </div>

      {/* Financeiro (só mostra se tem movimento) */}
      {(data.kpis.financeiro.qtd_a_pagar_prox_3d > 0 || data.kpis.financeiro.qtd_a_receber_prox_3d > 0) && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FinanceiroCard tipo="pagar" d={data.kpis.financeiro} />
          <FinanceiroCard tipo="receber" d={data.kpis.financeiro} />
        </div>
      )}

      {/* Gráficos financeiros: contas a pagar, conciliação e bancos */}
      {fin && <FinanceiroGraficos fin={fin} />}

      {/* Gráficos de Contas a Receber */}
      {fin?.receber_resumo && <ContasReceberGraficos fin={fin} />}
    </div>
  )
}

const CORES_REC_AGING = ['#7f1d1d', '#b91c1c', '#ef4444', '#10b981', '#0ea5e9', '#3b82f6', '#6366f1']
const CORES_FORMA: Record<string, string> = {
  boleto: '#0ea5e9', pix: '#10b981', dinheiro: '#22c55e', cheque: '#f59e0b',
  prazo: '#8b5cf6', cartao_credito: '#ec4899', cartao_debito: '#f43f5e', outros: '#64748b',
}

function ContasReceberGraficos({ fin }: { fin: GraficosFin }) {
  const r = fin.receber_resumo!
  const aging = fin.receber_aging || []
  const porForma = fin.receber_por_forma || []
  const top = fin.top_inadimplentes || []

  return (
    <div className="space-y-4">
      <h2 className="font-bold text-ink-900 flex items-center gap-2 text-lg">
        <ArrowUpRight size={18} className="text-emerald-600" /> Contas a Receber
      </h2>

      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <KpiCardCR titulo="Total em aberto" valor={r.total_vl} qtd={r.total_qt} cor="emerald" />
        <KpiCardCR titulo="Atrasados" valor={r.atrasado_vl} qtd={r.atrasado_qt} cor="red" />
        <KpiCardCR titulo="Vencem em 7d" valor={r.proximos_7d_vl} qtd={r.proximos_7d_qt} cor="amber" />
        <KpiCardCR titulo="Vencem em 15d" valor={r.proximos_15d_vl} qtd={r.proximos_15d_qt} cor="blue" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Aging (envelhecimento) */}
        <div className="card p-4 lg:col-span-2">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-bold text-ink-900 flex items-center gap-2">
              <Calendar size={16} className="text-brand-600" /> Aging — em dias
            </h3>
            <Link to="/financeiro/receber" className="text-xs text-brand-600 hover:underline">ver</Link>
          </div>
          {aging.some(a => a.valor > 0) ? (
            <div style={{ height: 220 }}>
              <ResponsiveContainer>
                <BarChart data={aging} margin={{ top: 4, right: 8, left: 8, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                  <XAxis dataKey="faixa" tick={{ fontSize: 9 }} interval={0} />
                  <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v} />
                  <Tooltip formatter={(v: any) => [formatMoney(v), 'Saldo']} />
                  <Bar dataKey="valor" radius={[4, 4, 0, 0]}>
                    {aging.map((_, i) => <Cell key={i} fill={CORES_REC_AGING[i]} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          ) : <EmptyState icon={CheckCircle2} msg="Nada a receber em aberto" cor="emerald" />}
        </div>

        {/* Por forma de cobrança */}
        <div className="card p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-bold text-ink-900 flex items-center gap-2">
              <PieIcon size={16} className="text-purple-600" /> Por cobrança
            </h3>
          </div>
          {porForma.length > 0 ? (
            <>
              <div style={{ height: 180 }}>
                <ResponsiveContainer>
                  <PieChart>
                    <Pie data={porForma} dataKey="valor" nameKey="label" cx="50%" cy="50%"
                      innerRadius={40} outerRadius={70} paddingAngle={2}>
                      {porForma.map((f, i) => (
                        <Cell key={i} fill={CORES_FORMA[f.forma] || CORES_FORMA.outros} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(v: any, _n: any, p: any) => [formatMoney(v), p.payload.label]} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="space-y-1 mt-2 text-xs">
                {porForma.slice(0, 6).map(f => (
                  <div key={f.forma} className="flex justify-between items-center">
                    <span className="flex items-center gap-1.5">
                      <span className="w-2 h-2 rounded-full" style={{ background: CORES_FORMA[f.forma] || CORES_FORMA.outros }} />
                      {f.label}
                      <span className="text-ink-400">({f.qtd})</span>
                    </span>
                    <span className="font-medium">{formatMoney(f.valor)}</span>
                  </div>
                ))}
              </div>
            </>
          ) : <EmptyState icon={CheckCircle2} msg="Nada a receber" cor="emerald" />}
        </div>
      </div>

      {/* Top 10 inadimplentes */}
      {top.length > 0 && (
        <div className="card p-4">
          <h3 className="font-bold text-ink-900 flex items-center gap-2 mb-3">
            <AlertTriangle size={16} className="text-red-600" /> Top 10 clientes com maior saldo em aberto
          </h3>
          <div className="space-y-1.5">
            {top.map((c, i) => {
              const max = top[0].valor
              const pct = (c.valor / max) * 100
              return (
                <Link key={c.pessoa_id} to={`/financeiro/receber?cliente=${c.pessoa_id}`}
                  className="block group hover:bg-ink-50 -mx-2 px-2 py-1 rounded">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-ink-700 truncate flex-1 mr-3">
                      <span className="text-ink-400 mr-2">{i + 1}.</span>{c.nome}
                    </span>
                    <span className="font-bold text-red-700">{formatMoney(c.valor)}</span>
                  </div>
                  <div className="h-1.5 bg-ink-100 rounded-full overflow-hidden mt-1">
                    <div className="h-full bg-red-500 transition-all"
                      style={{ width: `${pct}%` }} />
                  </div>
                </Link>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}

function KpiCardCR({ titulo, valor, qtd, cor }: { titulo: string; valor: number; qtd: number; cor: string }) {
  const cores: Record<string, string> = {
    emerald: 'border-emerald-200 bg-emerald-50 text-emerald-700',
    red: 'border-red-200 bg-red-50 text-red-700',
    amber: 'border-amber-200 bg-amber-50 text-amber-700',
    blue: 'border-blue-200 bg-blue-50 text-blue-700',
  }
  return (
    <div className={`rounded-xl border-2 p-3 ${cores[cor]}`}>
      <div className="text-xs font-medium opacity-80">{titulo}</div>
      <div className="text-xl font-bold mt-0.5">{formatMoney(valor)}</div>
      <div className="text-xs opacity-70">{qtd} título{qtd !== 1 ? 's' : ''}</div>
    </div>
  )
}

const CORES_AGING = ['#ef4444', '#f59e0b', '#3b82f6', '#10b981'] // vencido, 7d, 30d, +30d
const CORES_BANCO = ['#6366f1', '#0ea5e9', '#10b981', '#f59e0b', '#8b5cf6', '#ec4899', '#14b8a6']

function FinanceiroGraficos({ fin }: { fin: GraficosFin }) {
  const totalPagar = fin.pagar_aging.reduce((s, b) => s + b.valor, 0)
  const totalConc = fin.conciliacao.conciliados + fin.conciliacao.pendentes
  const concData = [
    { nome: 'Conciliados', valor: fin.conciliacao.conciliados, cor: '#10b981' },
    { nome: 'Pendentes', valor: fin.conciliacao.pendentes, cor: '#f59e0b' },
  ]
  const saldoTotal = fin.bancos.reduce((s, b) => s + b.saldo, 0)

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
      {/* Contas a pagar por vencimento */}
      <div className="card p-4">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-bold text-ink-900 flex items-center gap-2">
            <ArrowDownRight size={16} className="text-red-600" /> Contas a pagar por vencimento
          </h2>
          <Link to="/financeiro/pagar" className="text-xs text-brand-600 hover:underline">ver</Link>
        </div>
        {totalPagar > 0 ? (
          <>
            <div style={{ height: 200 }}>
              <ResponsiveContainer>
                <BarChart data={fin.pagar_aging} margin={{ top: 4, right: 8, left: 8, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                  <XAxis dataKey="faixa" tick={{ fontSize: 10 }} />
                  <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v} />
                  <Tooltip formatter={(v: any) => [formatMoney(v), 'A pagar']} />
                  <Bar dataKey="valor" radius={[4, 4, 0, 0]}>
                    {fin.pagar_aging.map((_, i) => <Cell key={i} fill={CORES_AGING[i % CORES_AGING.length]} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="flex justify-between text-xs border-t border-ink-100 pt-2 mt-1">
              <span className="text-ink-500">Total em aberto</span>
              <span className="font-bold text-red-700">{formatMoney(totalPagar)}</span>
            </div>
          </>
        ) : <EmptyState icon={CheckCircle2} msg="Nada a pagar em aberto" cor="emerald" />}
      </div>

      {/* Conciliação */}
      <div className="card p-4">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-bold text-ink-900 flex items-center gap-2">
            <CheckCircle2 size={16} className="text-emerald-600" /> Conciliação bancária
          </h2>
          <Link to="/financeiro/conciliacao" className="text-xs text-brand-600 hover:underline">ver</Link>
        </div>
        {totalConc > 0 ? (
          <div className="flex gap-4 items-center">
            <div style={{ width: 150, height: 150 }}>
              <ResponsiveContainer>
                <PieChart>
                  <Pie data={concData} dataKey="valor" nameKey="nome" cx="50%" cy="50%"
                    innerRadius={38} outerRadius={68} label={(e: any) => `${(e.percent * 100).toFixed(0)}%`} labelLine={false}>
                    {concData.map((c, i) => <Cell key={i} fill={c.cor} />)}
                  </Pie>
                  <Tooltip formatter={(v: any) => [`${v} lançamento(s)`, '']} />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="flex-1 space-y-2">
              {concData.map((c) => (
                <div key={c.nome} className="flex items-center gap-2 text-sm">
                  <span className="w-3 h-3 rounded" style={{ background: c.cor }} />
                  <span className="flex-1">{c.nome}</span>
                  <span className="font-bold text-ink-900">{c.valor}</span>
                </div>
              ))}
              <p className="text-xs text-ink-400 pt-1 border-t border-ink-100">
                {((fin.conciliacao.conciliados / totalConc) * 100).toFixed(0)}% conciliado
              </p>
            </div>
          </div>
        ) : <EmptyState icon={Info} msg="Sem lançamentos a conciliar" />}
      </div>

      {/* Saldo por banco */}
      <div className="card p-4">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-bold text-ink-900 flex items-center gap-2">
            <DollarSign size={16} className="text-indigo-600" /> Saldo por banco
          </h2>
          <Link to="/financeiro/conciliacao" className="text-xs text-brand-600 hover:underline">ver</Link>
        </div>
        {fin.bancos.length > 0 ? (
          <>
            <div style={{ height: 200 }}>
              <ResponsiveContainer>
                <BarChart data={fin.bancos} layout="vertical" margin={{ top: 4, right: 12, left: 8, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" horizontal={false} />
                  <XAxis type="number" tick={{ fontSize: 10 }} tickFormatter={(v) => v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v} />
                  <YAxis type="category" dataKey="nome" tick={{ fontSize: 10 }} width={80} />
                  <Tooltip formatter={(v: any) => [formatMoney(v), 'Saldo']} />
                  <Bar dataKey="saldo" radius={[0, 4, 4, 0]}>
                    {fin.bancos.map((b, i) => <Cell key={i} fill={b.cor || CORES_BANCO[i % CORES_BANCO.length]} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="flex justify-between text-xs border-t border-ink-100 pt-2 mt-1">
              <span className="text-ink-500">Saldo total</span>
              <span className={`font-bold ${saldoTotal >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>{formatMoney(saldoTotal)}</span>
            </div>
          </>
        ) : <EmptyState icon={DollarSign} msg="Nenhuma conta bancária" />}
      </div>
    </div>
  )
}

// ============ COMPONENTES ============

// Link do card de vendas → tela de Vendas JÁ FILTRADA (FIX 06/07: antes ia pra /vendas
// sem filtro e parecia "vazio"). Aplica período (hoje/mês/mês anterior) + canal PDV (balcão).
function qsVendas(periodo: 'hoje' | 'mes' | 'mes_anterior'): string {
  if (periodo === 'mes_anterior') {
    const now = new Date()
    const ini = new Date(now.getFullYear(), now.getMonth() - 1, 1)
    const fim = new Date(now.getFullYear(), now.getMonth(), 0)  // último dia do mês anterior
    const f = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
    return `/vendas?periodo=custom&canal=balcao&de=${f(ini)}&ate=${f(fim)}`
  }
  return `/vendas?periodo=${periodo}&canal=balcao`
}

function KpiCard({ titulo, valor, subtitulo, variacao, comparacao, icon: Icon, cor, link }: any) {
  const cores: Record<string, string> = {
    emerald: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    blue: 'bg-blue-50 text-blue-700 border-blue-200',
    violet: 'bg-violet-50 text-violet-700 border-violet-200',
    teal: 'bg-teal-50 text-teal-700 border-teal-200',
    red: 'bg-red-50 text-red-700 border-red-200',
  }
  const variacaoColor = variacao === null ? 'text-ink-400'
    : variacao > 0 ? 'text-emerald-700'
    : variacao < 0 ? 'text-red-700'
    : 'text-ink-500'

  const inner = (
    <div className="card p-4 h-full hover:shadow-md transition-shadow relative overflow-hidden">
      <div className={`absolute top-0 right-0 w-16 h-16 rounded-bl-full opacity-10 ${cores[cor]}`} />
      <div className="flex items-start justify-between relative">
        <div className="flex-1 min-w-0">
          <p className="text-xs font-semibold uppercase text-ink-500 mb-1">{titulo}</p>
          <p className="text-2xl font-bold text-ink-900 truncate">{valor}</p>
          <p className="text-xs text-ink-500 mt-1 truncate">{subtitulo}</p>
          {variacao !== null && (
            <div className={`flex items-center gap-1 mt-2 text-xs font-semibold ${variacaoColor}`}>
              {variacao > 0 && <TrendingUp size={12} />}
              {variacao < 0 && <TrendingDown size={12} />}
              {variacao === 0 && <Minus size={12} />}
              {variacao > 0 ? '+' : ''}{variacao}% <span className="font-normal text-ink-500">{comparacao}</span>
            </div>
          )}
        </div>
        <div className={`p-2 rounded-lg border ${cores[cor]}`}>
          <Icon size={18} />
        </div>
      </div>
    </div>
  )
  return link ? <Link to={link} className="block">{inner}</Link> : inner
}

function FluxoBadge({ icon: Icon, label, valor, cor }: any) {
  const cores: Record<string, string> = {
    ink: 'bg-ink-100 text-ink-700',
    purple: 'bg-purple-100 text-purple-700',
    indigo: 'bg-indigo-100 text-indigo-700',
    orange: 'bg-orange-100 text-orange-700',
    teal: 'bg-teal-100 text-teal-700',
    cyan: 'bg-cyan-100 text-cyan-700',
  }
  return (
    <div className={`rounded-lg p-2 text-center ${cores[cor]}`}>
      <Icon size={14} className="mx-auto mb-1" />
      <p className="text-[10px] font-semibold uppercase">{label}</p>
      <p className="text-lg font-bold">{valor}</p>
    </div>
  )
}

function FinanceiroCard({ tipo, d }: any) {
  const isPagar = tipo === 'pagar'
  const valor = isPagar ? d.a_pagar_prox_3d : d.a_receber_prox_3d
  const qtd = isPagar ? d.qtd_a_pagar_prox_3d : d.qtd_a_receber_prox_3d
  const atrasado = isPagar ? d.a_pagar_atrasado : d.a_receber_atrasado
  return (
    <Link to={isPagar ? '/financeiro/pagar' : '/financeiro/receber'}
      className={`card p-4 border-2 hover:shadow-md transition-shadow ${
        atrasado > 0 ? 'border-red-300 bg-red-50/50' : 'border-ink-200'
      }`}>
      <h3 className="font-semibold text-sm flex items-center gap-2 mb-2">
        {isPagar ? <ArrowDownRight size={14} className="text-red-600" /> : <ArrowUpRight size={14} className="text-emerald-600" />}
        {isPagar ? 'A pagar' : 'A receber'} (próximos 3 dias)
      </h3>
      <p className={`text-2xl font-bold ${isPagar ? 'text-red-700' : 'text-emerald-700'}`}>{formatMoney(valor)}</p>
      <p className="text-xs text-ink-500">{qtd} lançamento(s)</p>
      {atrasado > 0 && (
        <p className="text-xs font-semibold text-red-600 mt-2 flex items-center gap-1">
          <AlertCircle size={11} /> Atrasado: {formatMoney(atrasado)}
        </p>
      )}
    </Link>
  )
}

function EmptyState({ icon: Icon, msg, cor = 'ink' }: any) {
  return (
    <div className={`flex flex-col items-center justify-center py-10 text-${cor}-400`}>
      <Icon size={28} className="mb-2 opacity-60" />
      <p className="text-sm">{msg}</p>
    </div>
  )
}

// Estilo "Executivo" — hero com gradiente + tiles de vidro (números grandes)
function HeroExecutivo({ data }: { data: Dash }) {
  const k = data.kpis
  const tiles = [
    { label: 'Vendas hoje', valor: formatMoney(k.vendas_hoje.valor), sub: `${k.vendas_hoje.qtd_pedidos} pedidos · ticket ${formatMoney(k.vendas_hoje.ticket_medio || 0)}`, vari: k.vendas_hoje.variacao_pct, icon: DollarSign, to: qsVendas('hoje') },
    { label: 'Vendas no mês', valor: formatMoney(k.vendas_mes.valor), sub: `${k.vendas_mes.qtd_pedidos} pedidos`, vari: k.vendas_mes.variacao_pct, icon: Calendar, to: qsVendas('mes') },
    { label: 'Mês anterior', valor: formatMoney(k.vendas_mes_anterior.valor), sub: `${k.vendas_mes_anterior.qtd_pedidos} pedidos`, vari: null as number | null, icon: TrendingUp, to: qsVendas('mes_anterior') },
    { label: 'NFs autorizadas hoje', valor: String(k.nfs.autorizadas_hoje), sub: (k.nfs.pendentes || k.nfs.rejeitadas) ? `${k.nfs.pendentes} pend · ${k.nfs.rejeitadas} rej` : 'Todas processadas', vari: null as number | null, icon: FileText, to: '/fiscal' },
  ]
  return (
    <div className="relative overflow-hidden rounded-2xl p-6 lg:p-8 text-white shadow-xl bg-gradient-to-br from-brand-600 via-indigo-600 to-violet-700">
      <div className="absolute -top-16 -right-16 w-64 h-64 rounded-full bg-white/10 blur-2xl" />
      <div className="absolute -bottom-24 -left-12 w-72 h-72 rounded-full bg-white/5 blur-3xl" />
      <div className="relative">
        <div className="flex items-center justify-between flex-wrap gap-2 mb-5">
          <div>
            <div className="text-[11px] uppercase tracking-[0.2em] text-white/70 font-semibold">Visão Executiva</div>
            <h1 className="text-2xl lg:text-3xl font-extrabold">Seu negócio agora</h1>
          </div>
          <div className="text-xs text-white/70 flex items-center gap-1">
            <Activity size={13} /> Atualizado {new Date(data.gerado_em).toLocaleTimeString('pt-BR')}
          </div>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 lg:gap-4">
          {tiles.map((t, i) => {
            const Icon = t.icon
            const up = t.vari != null && t.vari >= 0
            return (
              <Link key={i} to={t.to}
                className="group rounded-xl bg-white/10 hover:bg-white/20 backdrop-blur-sm border border-white/15 p-4 transition">
                <div className="flex items-center justify-between mb-2">
                  <Icon size={18} className="text-white/80" />
                  {t.vari != null && (
                    <span className={`text-[11px] font-semibold px-1.5 py-0.5 rounded-full flex items-center gap-0.5 ${up ? 'bg-emerald-400/25 text-emerald-50' : 'bg-red-400/30 text-red-50'}`}>
                      {up ? <ArrowUpRight size={11} /> : <ArrowDownRight size={11} />}{Math.abs(t.vari).toFixed(0)}%
                    </span>
                  )}
                </div>
                <div className="text-2xl lg:text-[28px] font-extrabold leading-tight tracking-tight">{t.valor}</div>
                <div className="text-xs text-white/80 mt-1 font-medium">{t.label}</div>
                <div className="text-[11px] text-white/55 truncate">{t.sub}</div>
              </Link>
            )
          })}
        </div>
      </div>
    </div>
  )
}
